import { useEffect } from 'react'
import useUnreadMessagesContext from '@contexts/unreadMessagesContext'
import useScrollTriggerContext from '@contexts/scrollTriggerContext'
import useUnseenNotifications from '@hooks/useUnseenNotifications'
import theme, { appBottombar, colors, size } from '@styles/theme'
import useModalContext from '@contexts/modalContext'
import useCurrentPath from '@hooks/useCurrentPath'
import useStateRef from 'react-usestateref'
import { Link } from 'react-router-dom'
import styled from '@emotion/styled'
import {
  BottomNavigation,
  BottomNavigationAction,
  ButtonBase,
  IconButton
} from '@mui/material'
import {
  PiBellFill,
  PiChatCircleTextFill,
  PiMapPinFill,
  PiMapPinBold,
  PiBellBold,
  PiChatCircleTextBold,
  PiPlusBold,
} from 'react-icons/pi'
import {
  TbHomeFilled,
  TbMapPin,
  TbMapPinFilled,
  TbHome,
} from 'react-icons/tb'
import { PostActionBtn } from '@elements/FloatingActionBtn'

const StyledNav = styled(BottomNavigation)`
  position: absolute;
  // NOTE: Attempting to debug window.location.reload positioning issue
  bottom: 0;
  // top: calc(100dvh - ${appBottombar});

  display: flex;
  justify-content: space-between;

  width: 100%;
  height: ${appBottombar};
  z-index: 1000;
  padding: 0 ${size(1.5)};

  border-top: 0.5px solid rgba(255,255,255, 0.2);
  ${theme.glassBackgruond(0.9)}

  will-change: transform;
  // transform: translate3d(0, 0, 0);
`

const FloatButton = styled(ButtonBase)`
  position: fixed;
  bottom: ${size(7.875)}; // 71px (51px for bottom bar, 20px for margin)
  right: ${size(2.5)}; // 20px
  border-radius: 100%;
  box-shadow: 0px 2px 5px rgba(0,0,0, 0.35);
`

const PostButton = styled(PostActionBtn)`
  position: fixed;
  bottom: ${size(7.875)}; // 71px (51px for bottom bar, 20px for margin)
  right: ${size(2.5)}; // 20px
  border-radius: 100%;
  box-shadow: 0px 2px 5px rgba(0,0,0, 0.35);
`
  
const FloatButtonIcon = styled.img`
  height: ${size(7)};
  width: ${size(7)};
`

const NavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

// Don't render if any o the below pages
const excludedPages = new Set([
  'landing', 'login', 'posts', 'social-profile',
  'settings', 'password_reset', 'subscription',
  'settings/manage_subscription', 'about'
])

const newPostPages = new Set([
  'home', 'notifications'
])

const BottomNav = () => {
  const { newPost } = useModalContext()
  const { unreadMessages, hasUnreadMessages } = useUnreadMessagesContext()
  const { scrollTrigger, setScrollTrigger } = useScrollTriggerContext()
  const [selectedItem, setSelectedItem, selectedItemRef] = useStateRef(null)
  const [unseenNotifications] = useUnseenNotifications(selectedItem, selectedItemRef)
  const currentPath = useCurrentPath()

  // sets the selected item after cheking for transforms on the path we may want
  // WARNING: the actual routes are being matched in the useCurrentPath hook. We need to add new routes to that config
  useEffect(() => {
    const transformHash = {
      'posts/:id': 'posts',
      'profiles/:id': 'social-profile',
      'map': 'nearby-map',
      'grid': 'nearby-grid',
    }
    setSelectedItem(transformHash[currentPath] || currentPath)
  }, [currentPath])

  // TODO: refactor these into some kind of function
  const HomeIcon = !selectedItem || selectedItem === 'home' ? TbHomeFilled : TbHome
  // const HomeIcon = selectedItem === 'home' ? PiHouseFill : PiHouseBold
  // const NearbyIcon = !selectedItem || (selectedItem === 'nearby-map') || (selectedItem === 'nearby-grid') ? PiMapPinFill : PiMapPinBold
  const NearbyIcon = !selectedItem || (selectedItem === 'nearby-map') || (selectedItem === 'nearby-grid') ? TbMapPinFilled : TbMapPin
  // const NotificationIcon = selectedItem === 'notifications' ? TbBellFilled : TbBell
  const NotificationIcon = selectedItem === 'notifications' ? PiBellFill : PiBellBold
  // const MessageIcon = selectedItem === 'conversations' ? TbBubbleTextFilled : TbBubbleText
  const MessageIcon = selectedItem === 'conversations' ? PiChatCircleTextFill : PiChatCircleTextBold

  // This makes sure the navbar is visible every time the screen changes,
  // and that it doesn't get ou of view for screens that don't scroll (like the map)
  useEffect(() => {
    setScrollTrigger(false);
  }, [selectedItem])

  if (excludedPages.has(selectedItem)) return;
  return (
    <StyledNav className={scrollTrigger ? 'animation__hide-bottom-nav' : 'animation__show-bottom-nav' }>
      <NavItem>
        <BottomNavigationAction
          sx={{ minWidth: '0 !important' }}
          to='/home'
          component={Link}
          icon={
            <div style={{ position: 'relative', height: size(3.15) }}>
              {!(selectedItem === 'home') && <div style={{ backgroundColor: colors.statusColors.right_now('primary'), height: size(1.25), width: size(1.25), position: 'absolute', top: size(-0.25), left: size(-0.25), borderRadius: '100px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid black' }} />}
              <HomeIcon style={{ flexShrink: 0, zIndex: 3, position: 'relative' }} size={size(3.15)} color='white'/>
            </div>
          }
        />
      </NavItem>
      <NavItem>
        <BottomNavigationAction
          sx={{ minWidth: '0 !important' }}
          to='/map'
          component={Link}
          icon={
            <div style={{ height: size(3.15) }}>
              <NearbyIcon style={{ flexShrink: 0 }} size={size(3.2)} color='white'/>
            </div>
          }
        />
      </NavItem>
      <NavItem>
        <BottomNavigationAction
          sx={{ minWidth: '0 !important' }}
          onClick={() => newPost.open()}
          icon={
            // <IconButton component='div' style={{ backgroundColor: '#31303d', padding: size(1.25) }}>
            <IconButton component='div' style={{ backgroundColor: '#333333', padding: size(1.25) }}>
              <PiPlusBold style={{ flexShrink: 0 }} size={size(2)} color='white' />
            </IconButton>
          }
        />
      </NavItem>
      <NavItem>
        <BottomNavigationAction
          sx={{ minWidth: '0 !important' }}
          to='/notifications'
          component={Link}
          icon={
            <div style={{ position: 'relative', height: size(3.15) }}>
              <NotificationIcon style={{ flexShrink: 0 }} size={size(3.15)} color='white'/>
              {unseenNotifications > 0 &&
                <div style={{ backgroundColor: colors.statusColors.right_now('primary'), height: size(2.25), width: size(2.25), position: 'absolute', top: size(-0.6), right: size(-0.5), borderRadius: '100px', color: 'white', fontSize: unseenNotifications < 10 ? size(1.15) : size(0.9), fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid black' }}>
                  {unseenNotifications < 100 ? unseenNotifications : 99}
                </div>
              }
            </div>
          }
        />
      </NavItem>
      <NavItem>
        <BottomNavigationAction
          sx={{ minWidth: '0 !important' }}
          to='/conversations'
          component={Link}
          icon={
            <div style={{ position: 'relative', height: size(3.15) }}>
              <MessageIcon style={{ flexShrink: 0 }} size={size(3.15)} color='white'/>
              {hasUnreadMessages &&
                <div style={{ backgroundColor: colors.statusColors.right_now('primary'), height: size(2.25), width: size(2.25), position: 'absolute', top: size(-0.6), right: size(-0.5), borderRadius: '100px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid black' }} />
              }
            </div>
          }
        />
      </NavItem>
      {newPostPages.has(selectedItem) &&
        // <PostActionBtn
        //   style={{
        //       position: 'fixed',
        //       bottom: size(7.875), // 71px (51px for bottom bar, 20px for margin)
        //       right: size(2.5), // 20px
        //       boxShadow: '0px 2px 5px rgba(0,0,0, 0.35)',
        //   }}
        //   onClick={() => newPost.open()}
        //   className={scrollTrigger ? 'animation__pop-out' : 'animation__pop-in' }
        // />
        <FloatButton
          onClick={() => newPost.open()}
          className={scrollTrigger ? 'animation__pop-out' : 'animation__pop-in' }>
          <FloatButtonIcon src={'https://mandrake-app.b-cdn.net/dev/application/icons/plus-icon.svg'} />
        </FloatButton>
      }
    </StyledNav>
  )
}

export default BottomNav
